import {
  ActionButton,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Icon,
  IconButton,
  Label,
  Link,
} from "@fluentui/react";
import React, {
  ChangeEvent,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import { Attachment } from "../../types/types";
import { getFileExtension, getFileIcon, getIconColor } from "../../utils/utils";
import { useSelector } from "../../store/hooks";

const AttachmentListContainer = styled.ul`
  display: flex;
  flex-direction: column;
  margin-top: 5px;
`;

const AttachmentListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLighterAlt};
  width: 350px;
`;

const AttachmentName = styled.div`
  font-size: 14px;
  color: ${(p) => p.theme.palette.neutralPrimary};
  flex: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 300px;
`;

const DropZone = styled.div<{ isDragging: boolean }>`
  font-size: 14px;
  color: ${(p) => p.theme.palette.neutralPrimary};
  border: 1px dashed #888888;
  padding: 10px 8px 8px 8px;
  text-align: center;
  margin-top: 10px;
  display: inline;
  background-color: ${(p) => (p.isDragging ? "#888888" : "transparent")};
`;

const DocViewerContainer = styled.div`
  width: 100%;
  height: 80vh;
  min-height: 600px;
  overflow: auto;

  /* Ensure DocViewer and its children fill the container */
  #react-doc-viewer {
    height: 100% !important;
  }

  #proxy-renderer {
    height: 100% !important;
  }

  #msdoc-renderer,
  #image-renderer,
  #pdf-renderer {
    height: 100% !important;
  }

  iframe {
    height: 100% !important;
    min-height: 550px;
  }
`;

type Props = {
  attachments: Attachment[];
  setAttachments: (attachments: Attachment[]) => void;
};

export const ReportAttachments = ({ attachments, setAttachments }: Props) => {
  const { t } = useTranslation();
  const refFileInput = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const report = useSelector((s) => s.reportPage.report);
  const userRole = useSelector((s) => s.userRole.userRole);
  const isReadOnly =
    report?.isReportDeactivated === true || userRole === "external";

  const areSameFile = (file1Name: string, file2Name: string) =>
    file1Name === file2Name;

  const documents = useMemo(() => {
    return attachments
      .filter((att) => att.attachmentPath)
      .map((att) => ({
        uri: att.attachmentPath as string,
        fileName: att.attachmentName,
        fileType: att.attachmentMimeType || undefined,
      }));
  }, [attachments]);

  const dialogContentProps = {
    type: DialogType.normal,
    title: t("ram.responsesPage.attachments.previewDocuments"),
    closeButtonAriaLabel: "Close",
  };

  const processFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const fileArr = Array.from(files);
      const newAttachments = fileArr
        .filter(
          (f) =>
            !attachments.some((a) => areSameFile(a.attachmentName, f.name)),
        )
        .map((f) => {
          let attachmentName = f.name;
          if (f.name.length > 128) {
            const ext = f.name.split(".").slice(-1).toString();
            attachmentName =
              f.name.slice(0, 128 - (ext.length + 1)) + "." + ext;
          }
          return {
            attachmentName,
            attachmentMimeType: f.type,
            attachmentContent: f,
            attachmentSize: f.size,
          } as any as Attachment;
        });
      setAttachments([...attachments, ...newAttachments]);
    },
    [attachments, setAttachments],
  );

  const onChangeUploadFiles = (e: ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => setIsDragging(false);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  };

  return (
    <div onDragLeave={onDragLeave} onDrop={onDrop} onDragOver={onDragOver}>
      <Label>{t("ram.reportForm.attachments.label")}</Label>
      {!isReadOnly && (
        <DropZone isDragging={isDragging}>
          <ActionButton
            iconProps={{ iconName: "CalculatorAddition" }}
            allowDisabledFocus
            onClick={() => refFileInput.current?.click()}
          >
            {isDragging
              ? t("ram.reportForm.attachments.dropFiles")
              : t("ram.reportForm.attachments.addAttachment")}
          </ActionButton>
        </DropZone>
      )}
      {documents.length > 0 && (
        <ActionButton
          iconProps={{ iconName: "View" }}
          allowDisabledFocus
          onClick={() => setIsPreviewDialogOpen(true)}
        >
          {t("ram.responsesPage.attachments.previewDocuments")}
        </ActionButton>
      )}

      <AttachmentListContainer>
        {attachments.map((att, index) => (
          <AttachmentListItem key={att.attachmentName}>
            <Icon
              iconName={getFileIcon(getFileExtension(att.attachmentName))}
              style={{
                color: getIconColor(getFileExtension(att.attachmentName)),
              }}
            />
            <AttachmentName>
              {att.attachmentPath ? (
                <Link href={att.attachmentPath} target="_blank">
                  {att.attachmentName}
                </Link>
              ) : (
                att.attachmentName
              )}
            </AttachmentName>
            {!isReadOnly && (
              <IconButton
                iconProps={{ iconName: "Cancel" }}
                onClick={() => {
                  setAttachments(attachments.filter((_, idx) => idx !== index));
                  if (refFileInput.current) refFileInput.current.value = "";
                }}
              />
            )}
          </AttachmentListItem>
        ))}
      </AttachmentListContainer>
      <input
        type="file"
        name="file"
        multiple
        style={{ display: "none" }}
        ref={refFileInput}
        onChange={onChangeUploadFiles}
      />
      <Dialog
        hidden={!isPreviewDialogOpen}
        onDismiss={() => setIsPreviewDialogOpen(false)}
        dialogContentProps={dialogContentProps}
        minWidth={800}
        maxWidth={1200}
      >
        <DocViewerContainer>
          <DocViewer
            documents={documents}
            pluginRenderers={DocViewerRenderers}
            config={{
              header: {
                disableHeader: false,
                disableFileName: false,
              },
            }}
          />
        </DocViewerContainer>
        <DialogFooter>
          <DefaultButton
            onClick={() => setIsPreviewDialogOpen(false)}
            text={t("ram.reportDocumentDialogDownloadClose.label")}
          />
        </DialogFooter>
      </Dialog>
    </div>
  );
};
